import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type React from "react";
import { useMemo } from "react";

import type { FeelingAtHomeQuestionResponseDto, FeelingAtHomeSurveyQuestionV2Dto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Table } from "@/components/Table/Table";
import { useTranslation } from "@/translations";

type FahHistoryQuestionResponsesModalProps = ModalBaseProps & {
  order: number;
  question: FeelingAtHomeSurveyQuestionV2Dto;
};
export function FahHistoryQuestionResponsesModal({
  order,
  question,
  isOpened,
  onOpenChange,
}: FahHistoryQuestionResponsesModalProps): React.ReactNode {
  const { i18n, t } = useTranslation();

  const totalResponses = question.responseCount;
  const columns = useMemo(() => {
    const helper = createColumnHelper<FeelingAtHomeQuestionResponseDto>();

    return [
      helper.accessor("rating", {
        header: t("component.surveys-question.details.table.header.rating"),
        cell: (cell) => (
          <span className="flex items-center gap-1 text-center">
            <span>{cell.getValue()}</span>
            <Star01Icon className="shrink-0 text-yellow-500" />
          </span>
        ),
        maxSize: 32,
      }),
      helper.accessor("answer", {
        header: t("component.surveys-question.details.table.header.open"),
        cell: (cell) => <span className="block py-0.5">{cell.getValue()}</span>,
      }),
      helper.accessor("user.fullName", {
        header: t("component.surveys-question.details.table.header.username"),
        cell: (cell) => <span className="block truncate">{cell.getValue()}</span>,
        maxSize: 100,
      }),
      helper.accessor("answeredAt", {
        header: t("component.surveys-question.details.table.header.date"),
        cell: (cell) => <span>{formatDate(i18n, "datetimeShort", cell.getValue())}</span>,
        maxSize: 80,
      }),
    ];
  }, [i18n, t]);
  const tableInstance = useReactTable<FeelingAtHomeQuestionResponseDto>({
    columns,
    data: question.responses,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Modal.Root
      size="fullscreen"
      title={`${order}. ${question.ratingQuestionText}`}
      description={question.openQuestionText}
      {...{
        isOpened,
        onOpenChange,
      }}
    >
      <span className="text-caption">
        {t("component.surveys-question.details.responses", {
          count: totalResponses || 0,
        })}
      </span>
      {totalResponses > 0 && (
        <Table
          table={tableInstance}
          data-testid="survey-answer-list"
          getRowProps={() => ({
            "data-testid": "survey-answer-item",
          })}
        />
      )}
    </Modal.Root>
  );
}
